import { memo, useCallback, startTransition } from 'react';
import clx from 'classnames';
import { useNavigate } from 'react-router-dom';
import { Flame, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styles from './NavItem.module.scss';
import { ReactComponent as Support } from 'assets/svg/icons/support.svg';
import { ReactComponent as LogOut } from 'assets/svg/icons/esci.svg';

const addNomediv = (label) => {
  switch (label) {
    case 'Pagamenti':
      return 'payment';
    case 'Home':
      return 'home';
    case 'Recensioni':
      return 'reviews';
    case 'Pubblicità':
      return 'advertising';
    case 'Assistenza':
      return 'help';
    case 'App privata':
      return 'privata';
    case 'Shop':
      return 'shop';
    case 'Statistiche':
      return 'statistics';
    case 'Spese':
      return 'expenses';
    case 'Comunicazioni':
      return 'communications';
    default:
      return null;
  }
};

const openInNewTab = (url) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) newWindow.opener = null;
};

const NavItemContent = ({
  icon,
  label,
  premiumLocked,
  premiumLabel,
  showNewBadge,
  newBadgeLabel,
}) => (
  <>
    <div className={styles.vertical_marker} aria-hidden />
    <span
      className={styles.item__inner}
      title={premiumLocked ? premiumLabel : showNewBadge ? newBadgeLabel : undefined}
    >
      <span className={styles.item__icon}>
        {icon}
        {premiumLocked ? (
          <span className={styles.item__premium_badge} aria-hidden="true">
            <Flame
              className={styles.item__premium_flame}
              size={12}
              strokeWidth={2.25}
              fill="currentColor"
            />
          </span>
        ) : null}
        {showNewBadge && !premiumLocked ? (
          <span className={styles.item__new_badge} aria-hidden="true">
            <Sparkles
              className={styles.item__new_sparkle}
              size={11}
              strokeWidth={2.25}
            />
          </span>
        ) : null}
      </span>
      <span className={styles.item__copy}>
        <span className={styles.item__label}>{label}</span>
        {premiumLocked ? (
          <span className={styles.item__premium_row}>
            <Flame
              className={styles.item__premium_flame}
              size={16}
              strokeWidth={2.25}
              fill="currentColor"
            />
            <span className={styles.item__premium_text}>{premiumLabel}</span>
          </span>
        ) : null}
        {showNewBadge && !premiumLocked ? (
          <span className={styles.item__new_row}>
            <Sparkles
              className={styles.item__new_sparkle}
              size={14}
              strokeWidth={2.25}
            />
            <span className={styles.item__new_text}>{newBadgeLabel}</span>
          </span>
        ) : null}
      </span>
    </span>
  </>
);

const NavItem = memo(({
  navItem,
  activeItem,
  premiumLocked = false,
  onPremiumClick,
  premiumLabel,
  showNewBadge = false,
  newBadgeLabel,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const isActive =
    !premiumLocked &&
    (navItem.link === '/settings'
      ? activeItem === navItem.link ||
        (activeItem?.startsWith(`${navItem.link}/`) &&
          !activeItem?.startsWith('/settings/expenses'))
      : activeItem === navItem.link ||
        activeItem?.startsWith(`${navItem.link}/`) ||
        (navItem.link !== '/' && activeItem?.includes(navItem.link)));

  const handleClick = useCallback(
    (event) => {
      event.stopPropagation();
      if (premiumLocked) {
        onPremiumClick?.();
        onNavigate?.();
        return;
      }
      if (navItem.newTab) {
        openInNewTab(navItem.link);
      } else if (activeItem !== navItem.link) {
        // Navigazione immediata: non attendere fetch/render pesanti della pagina corrente.
        startTransition(() => {
          navigate(navItem.link);
        });
      }
      onNavigate?.();
    },
    [
      premiumLocked,
      onPremiumClick,
      onNavigate,
      navItem.newTab,
      navItem.link,
      activeItem,
      navigate,
    ],
  );

  return (
    <li
      className={clx(styles.nav__item, {
        [styles['nav-item__active']]: isActive,
        [styles.nav__item_premium]: premiumLocked,
        [styles.nav__item_new]: showNewBadge && !premiumLocked,
      })}
      data-nomediv={addNomediv(navItem.label)}
      onClick={handleClick}
    >
      <NavItemContent
        icon={navItem.icon}
        label={navItem.label}
        premiumLocked={premiumLocked}
        premiumLabel={premiumLabel}
        showNewBadge={showNewBadge}
        newBadgeLabel={newBadgeLabel}
      />
    </li>
  );
});

NavItem.displayName = 'NavItem';

const NavItemHelp = () => (
  <li
    className={clx(styles.nav__item, styles['nav-item__active'])}
    data-nomediv="help"
  >
    <NavItemContent icon={<Support />} label="Assistenza" />
  </li>
);

const NavItemLogout = memo(({ onClick }) => {
  const { t } = useTranslation();

  return (
    <li
      className={clx(styles.nav__item, styles.nav__item_logout)}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
    >
      <NavItemContent icon={<LogOut />} label={t('common.actions.logout')} />
    </li>
  );
});

NavItemLogout.displayName = 'NavItemLogout';

export { NavItem, NavItemHelp, NavItemLogout };
